import { useCallback, useEffect, useState } from 'react';
import { auditsAPI, type AuditSummary } from '../../lib/api';
import { apiError } from '../audits/useAuditActions';

export interface AuditSummaryState {
  summary: AuditSummary | null;
  loading: boolean;
  error: string | null;
  reload: () => Promise<void>;
}

/**
 * A supplier's Audit Lite summary (last audit, valid until, open / overdue
 * actions) — the profile's Audits tab badge and cards.
 *
 * @param supplierId - the supplier company id
 * @param enabled - false when the Audits tab is not shown (nothing is fetched)
 * @returns summary, loading / error flags and `reload`
 * @example const { summary } = useAuditSummary(supplier.company, showAudits);
 */
export function useAuditSummary(supplierId: number, enabled: boolean): AuditSummaryState {
  const [summary, setSummary] = useState<AuditSummary | null>(null);
  const [loading, setLoading] = useState<boolean>(enabled);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (!enabled || !supplierId) {
      setSummary(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      setSummary(await auditsAPI.summary(supplierId));
    } catch (err: unknown) {
      console.error('Failed to load the audit summary:', err);
      setError(apiError(err, 'Could not load the audit summary.'));
    } finally {
      setLoading(false);
    }
  }, [supplierId, enabled]);

  useEffect(() => {
    void reload();
  }, [reload]);

  return { summary, loading, error, reload };
}

/**
 * Whether the profile shows the Audits tab: Audit Lite on, a customer-side or
 * Helix viewer (never a supplier, never a company's own profile), and the
 * tenant's profile layout not hiding it.
 */
export function canShowAuditsTab(opts: {
  auditLiteEnabled: boolean;
  /** Customer side or Helix (`useIsCustomer`). */
  isCustomer: boolean;
  isOwnCompany: boolean;
  layoutAllows: boolean;
}): boolean {
  return opts.auditLiteEnabled && opts.isCustomer && !opts.isOwnCompany && opts.layoutAllows;
}
